import { api, base, token } from '../../api.ts';
import { agentsApi } from '../../agents-api.ts';
import type { ProjectInfo } from '../../agents-api.ts';
import { h } from '../../ui.ts';
import { talkAudio } from '../../talk-audio.ts';
import type { TalkAudio, TalkState } from '../../talk-audio.ts';
import { counts, icon, itemCard, remember, screen } from './kit.ts';
import type { TalkSession, WorkItem } from './kit.ts';
import { chosenProject, projectSwitcher, talkButtonLabel } from './switcher.ts';
import { TalkScreen, orb } from './talk-screen.ts';

export { phoneLogin } from './login.ts';

/**
 * The talk (design-docs/0028, 0030): orb, captions and what she started; or, while driving, one status line and one
 * button. Nothing here is needed while driving: she says everything. The same screen on the phone and the desktop.
 */

let audio: TalkAudio | null = null;
let audioState: TalkState = 'stopped';

const inAppAudio = () =>
  !!(window as unknown as { Capacitor?: { Plugins?: { TalkAudio?: unknown } } }).Capacitor?.Plugins?.TalkAudio;
/** Re-renders in place: the phone's root, or the desktop shell's main column. */
const mount = (el: HTMLElement) =>
  (document.getElementById('main') ?? document.getElementById('app')!).replaceChildren(el);

/** An ended talk from Home, read-only; otherwise the live talk (in a browser, after a tap). */
export async function talkView(id?: string): Promise<HTMLElement> {
  if (id) {
    const s = await api.get<TalkSession>(`/sessions/${id}`);
    if (s.endedAt) return pastTalk(s);
  }
  if (!audio && !inAppAudio()) return startScreen(await agentsApi.projects().catch(() => [] as ProjectInfo[]));
  return liveTalk();
}

// a browser needs a tap before it may use the microphone and play audio
function startScreen(projects: ProjectInfo[]): HTMLElement {
  const go = async () => {
    audio = talkAudio();
    mount(await liveTalk());
  };
  const label = h('span', {}, talkButtonLabel(projects, false));
  return screen(
    header(null, null),
    h('div', { class: 'p-orb-wrap' }, orb(false)),
    h(
      'p',
      { class: 'p-lead' },
      'Say what should change. She files it, starts agents and tells you when a preview is ready.',
    ),
    h('div', { class: 'p-grow' }),
    h(
      'div',
      { class: 'p-foot' },
      projects.length ? projectSwitcher(projects, () => (label.textContent = talkButtonLabel(projects, false))) : null,
      h('button', { class: 'p-primary', onclick: () => void go() }, icon.mic(20), label),
    ),
  );
}

function header(startedAt: number | null, about: string | null, chips?: HTMLElement): HTMLElement {
  const timer = h('span', {}, startedAt ? 'LIVE' : 'READY');
  if (startedAt) {
    const tick = () => {
      const s = Math.floor((Date.now() - startedAt) / 1000);
      timer.textContent = `LIVE ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    };
    tick();
    const t = setInterval(() => (timer.isConnected ? tick() : clearInterval(t)), 1000);
  }
  return h(
    'div',
    {},
    h(
      'header',
      { class: 'p-head' },
      h('a', { class: 'p-icon', href: '#/', 'aria-label': 'Home (the talk keeps going)' }, icon.down()),
      h('span', { class: 'p-pill' }, h('span', { class: `p-live ${startedAt ? 'on' : ''}` }), timer),
      h('span', { class: 'p-icon-space' }),
    ),
    about ? h('div', { class: 'p-about' }, icon.folder(12), about) : null,
    chips ?? null,
  );
}

async function stopTalk(id: string | null) {
  await audio?.stop();
  audio = null;
  audioState = 'stopped';
  document.body.classList.remove('driving');
  if (id) await api.post(`/sessions/${id}/end`).catch(() => {});
  location.hash = '#/';
}

/** Captions so far, live events, the poll, and the audio. */
async function connect(t: TalkScreen) {
  const full = await api.get<{ journal: Array<{ event: string; payload: { speaker?: string; text?: string } }> }>(
    `/sessions/${t.s.id}`,
  );
  for (const j of full.journal.filter((r) => r.event === 'transcript.segment').slice(-4))
    t.caption(j.payload.speaker ?? '', j.payload.text ?? '');
  await t.refresh();
  const poll = setInterval(() => (t.captions.isConnected ? void t.refresh() : clearInterval(poll)), 3000);
  const off = api.events((name, payload) => {
    const p = payload as { sessionId?: string; text?: string; speaker?: string; final?: boolean };
    if (p.sessionId !== t.s.id) return;
    if (name === 'transcript.segment' && p.final !== false) t.caption(p.speaker ?? '', p.text ?? '');
    if (name === 'channel.message') void t.refresh();
    if (name === 'session.ended') {
      off();
      void stopTalk(null);
    }
  });
  window.addEventListener('hashchange', off, { once: true });

  audio ??= talkAudio();
  audio.onState((st) => {
    audioState = st;
    t.onState(st);
  });
  if (audioState !== 'stopped') return;
  try {
    await audio.start(`${base().replace(/^http/, 'ws')}/sessions/${t.s.id}/audio?token=${encodeURIComponent(token())}`);
  } catch (err) {
    t.state.textContent = err instanceof Error ? err.message : String(err);
    t.state.classList.add('p-error');
  }
}

async function liveTalk(): Promise<HTMLElement> {
  const projects = await agentsApi.projects().catch(() => [] as ProjectInfo[]);
  const project = chosenProject(projects);
  const t = new TalkScreen(await api.post<TalkSession>('/talk', project ? { project } : {}), audioState);
  const driving = remember('driving') === 'on';
  document.body.classList.toggle('driving', driving);
  await connect(t);
  const toggle = () => {
    remember('driving', driving ? 'off' : 'on');
    void liveTalk().then(mount);
  };
  const end = () => void stopTalk(t.s.id);
  const about = projects.find((p) => p.slug === project)?.name ?? null;
  return driving ? drivingScreen(t, end, toggle) : talkScreen(t, about, end, toggle);
}

function talkScreen(t: TalkScreen, about: string | null, end: () => void, toggle: () => void): HTMLElement {
  return screen(
    header(t.s.startedAt, about, t.chips),
    h('div', { class: 'p-orb-wrap' }, t.orb),
    t.state,
    t.captions,
    t.cards,
    h('div', { class: 'p-grow' }),
    h(
      'div',
      { class: 'p-controls' },
      h('span', { class: 'p-round-space' }),
      h('button', { class: 'p-end', 'aria-label': 'End talk', onclick: end }, icon.hang()),
      h('button', { class: 'p-round', 'aria-label': 'Driving mode', onclick: toggle }, icon.car()),
    ),
  );
}

function drivingScreen(t: TalkScreen, end: () => void, toggle: () => void): HTMLElement {
  return screen(
    h('span', { class: 'p-pill p-center p-drive-pill' }, icon.car(14), 'DRIVING · VOICE ONLY'),
    h('div', { class: 'p-drive' }, t.orb, t.driveLine, h('p', {}, 'Previews wait until you’re parked.')),
    h(
      'div',
      { class: 'p-drive-actions' },
      h('button', { class: 'p-end-wide', onclick: end }, icon.hang(30), 'End talk'),
      h('button', { class: 'p-icon p-center p-wide', onclick: toggle }, 'Show the screen'),
    ),
  );
}

async function pastTalk(s: TalkSession): Promise<HTMLElement> {
  const items =
    (await api.get<{ items: WorkItem[] }>(`/sessions/${s.id}/work`).catch(() => null))?.items ?? ([] as WorkItem[]);
  const mins = Math.max(1, Math.round(((s.endedAt ?? s.startedAt) - s.startedAt) / 60000));
  const when = new Date(s.startedAt).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  const ready = counts(items).ready;
  return screen(
    h('header', { class: 'p-head' }, h('a', { class: 'p-icon', href: '#/', 'aria-label': 'Back' }, icon.back())),
    h('span', { class: 'p-mono p-greet' }, `TALK · ${when.toUpperCase()} · ${mins}M`),
    h(
      'h1',
      { class: 'p-title' },
      `${items.length} filed`,
      h('br'),
      h('span', {}, `${ready} preview${ready === 1 ? '' : 's'} ready`),
    ),
    h(
      'section',
      { class: 'p-section' },
      items.length
        ? h('div', { class: 'p-cards' }, ...items.map(itemCard))
        : h('div', { class: 'p-empty' }, 'Nothing was filed in this talk.'),
    ),
  );
}
